/**
 * PD-15142 — how a card is funded, which decides whether it may be surcharged.
 *
 * A surcharge may only be applied to a CREDIT card. Regulation II (Dodd-Frank)
 * prohibits surcharging debit — including signature debit processed as credit —
 * and the card networks extend the same restriction to prepaid. The application
 * previously stored only the brand (Visa, MasterCard, Amex), which says nothing
 * about funding, so every card was surcharged alike.
 *
 * Values mirror the gateway's own vocabulary so nothing is lost in translation:
 * NMI's Collect.js `category` field and its Card Type Lookup API both answer
 * with exactly these strings.
 */
export enum CardCategory {
	CREDIT = 'credit',
	DEBIT = 'debit',
	PREPAID = 'prepaid',
	CHARGE = 'charge',
	DEFERRED_DEBIT = 'deferred_debit',
	/** The gateway could not classify the card. */
	UNKNOWN = 'unknown',
	/** The classification service was unreachable. Distinct from UNKNOWN so a
	 *  transient outage can be retried rather than cached as a real answer. */
	UNAVAILABLE = 'unavailable',
}

export type CardCategoryValue = `${CardCategory}`;

/**
 * PD-15154 — how a card's funding type is named on screen.
 *
 * A fee is a whitelist of one: only a credit card may carry it. That is
 * correct, but on its own it leaves "No fee" unexplained, and the two reasons a
 * card carries no fee are not the same thing. A debit card may NEVER be
 * surcharged, so nothing about it will change. A card the gateway could not
 * identify simply has not been established as credit, and is not surcharged
 * because promising a fee we cannot justify is the worse error. Showing the
 * funding type lets someone looking at the screen tell those apart.
 *
 * `unavailable` is not listed: it is never stored (a gateway that could not
 * answer is retried rather than recorded), so it should not reach a display. It
 * falls through to the unclassified wording via `getCardCategoryLabel` if it
 * somehow does.
 */
export const CardCategoryLabels: Record<string, string> = {
	[CardCategory.CREDIT]: 'Credit card',
	[CardCategory.DEBIT]: 'Debit card',
	[CardCategory.PREPAID]: 'Prepaid card',
	[CardCategory.CHARGE]: 'Charge card',
	[CardCategory.DEFERRED_DEBIT]: 'Deferred debit card',
	[CardCategory.UNKNOWN]: 'Unclassified',
};

/**
 * The on-screen name for a card's funding type, or null when there is nothing
 * to say.
 *
 * Null for a method that is not a card: ACH, cash and cheque are not
 * surcharges under the network rules and keep whatever fee they are configured
 * with, so labelling them with a funding type would imply a restriction that
 * does not apply. Null too for a card stored before the funding type was
 * captured, rather than asserting "Unclassified" about a card nobody has
 * actually asked the gateway about yet.
 */
export const getCardCategoryLabel = (
	cardCategory: string | null | undefined,
): string | null => {
	if (cardCategory == null || cardCategory === '') {
		return null;
	}

	return CardCategoryLabels[cardCategory] ?? 'Unclassified';
};

/**
 * Whether a processing fee may be charged on a card of this category.
 *
 * Deliberately a whitelist of one. Everything else — debit, prepaid, charge
 * cards, an unresolved card, or a gateway we could not reach — gets no fee.
 * Absorbing 3% is a cost; surcharging a debit card is a federal violation, so
 * the two failure modes are not comparable and the default must be "no fee".
 *
 * `charge` cards (e.g. some Amex products) are excluded on the same principle:
 * they are not credit for surcharging purposes under the network rules, and
 * where the rules are ambiguous the safe reading is the one that does not take
 * the customer's money.
 */
export const isSurchargeableCardCategory = (
	category: string | null | undefined,
): boolean => category === (CardCategory.CREDIT as string);

/**
 * Whether a stored classification should be re-resolved before the card is used.
 *
 * A card with no classification has never been looked up. One recorded as
 * UNAVAILABLE was a gateway outage rather than an answer, so it is worth
 * asking again. UNKNOWN is a real answer from the gateway — the card could not
 * be classified — and re-asking on every use would just repeat the call, so it
 * is skipped here and retried on a slow cadence via
 * `isStaleUnknownCardCategory` instead.
 */
export const needsCardCategoryResolution = (
	category: string | null | undefined,
): boolean =>
	category == null ||
	category === '' ||
	category === (CardCategory.UNAVAILABLE as string);

/**
 * How long a stored UNKNOWN is trusted before the gateway is asked again.
 *
 * BIN databases fill in over time — a card the gateway could not classify at
 * entry may well classify a week later — so UNKNOWN is retried occasionally
 * rather than cached forever. Seven days keeps the retry traffic negligible
 * (one lookup per still-unknown card per week) while a card that becomes
 * classifiable starts carrying its fee within days, not never.
 */
export const UNKNOWN_CARD_CATEGORY_RETRY_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * Whether a stored UNKNOWN classification is old enough to re-ask the gateway.
 *
 * Reads the resolved-date the classification was stored with — no separate
 * retry bookkeeping. Re-resolving refreshes that date (an unknown answer is
 * re-stored), so the cadence is self-maintaining. An UNKNOWN with no recorded
 * date predates the date column and is treated as stale immediately.
 */
export const isStaleUnknownCardCategory = (
	category: string | null | undefined,
	resolvedDate: Date | null | undefined,
): boolean => {
	if (category !== (CardCategory.UNKNOWN as string)) {
		return false;
	}

	if (resolvedDate == null) {
		return true;
	}

	return Date.now() - resolvedDate.getTime() > UNKNOWN_CARD_CATEGORY_RETRY_MS;
};

/**
 * Reduce an untrusted value to a known card category, or null.
 *
 * Applied to anything arriving from outside — a tokenisation callback, an API
 * request body — before it is stored. A value nobody recognises must not reach
 * the surcharge decision: leaving it null means the card is resolved from the
 * gateway before first use, which is the honest outcome. Storing an unknown
 * string would instead be read as "already classified" and skip that lookup
 * forever.
 */
export const normalizeCardCategory = (
	value: string | null | undefined,
): CardCategoryValue | null => {
	if (value == null || value === '') {
		return null;
	}

	const normalized = value.trim().toLowerCase();
	const known = Object.values(CardCategory).find(
		(category) => (category as string) === normalized,
	);

	// `unavailable` is not an answer — it records that nobody could look — so it
	// is never stored, leaving the card to be retried.
	if (known == null || known === CardCategory.UNAVAILABLE) {
		return null;
	}

	return known;
};

/**
 * PD-16381 — the funding type to DISPLAY for a card payment, from the answers
 * on record for it, most specific first: the transaction's own stamp, then the
 * saved method's current classification.
 *
 * The first definite answer wins. A card's funding never changes, so a
 * classification that arrived after the payment (an unknown card re-asked a
 * week later) is as true of that payment as one stamped at the time. `unknown`
 * only when something looked and nothing definite is known; null when no
 * source says anything. Every value is normalized first, so a stray spelling
 * or `unavailable` is treated as no answer.
 */
export const resolveDisplayCardCategory = (
	...candidates: Array<string | null | undefined>
): CardCategoryValue | null => {
	const known = candidates
		.map(normalizeCardCategory)
		.filter((category): category is CardCategoryValue => category != null);

	return (
		known.find((category) => category !== CardCategory.UNKNOWN) ??
		known.at(0) ??
		null
	);
};

/**
 * PD-15014 — reduce a gateway's funding-source vocabulary to a CardCategory.
 *
 * Gateways answer the "how is this card funded" question in near-identical but
 * not identical dialects: NMI speaks this enum's own lowercase strings; Adyen
 * answers in uppercase with prepaid subtypes (PREPAID_RELOADABLE /
 * PREPAID_NONRELOADABLE) and ships a documented misspelling (DEFFERED_DEBIT —
 * verbatim in their additionalData spec, matched here alongside the correct
 * spelling defensively); CyberSource's BIN lookup answers in uppercase. One
 * mapper keeps every adapter's translation identical, and anything it does not
 * recognise becomes null — never a guess — so an unrecognised dialect can only
 * ever cost a fee, not illegally charge one.
 */
export const cardCategoryFromGatewayFundingSource = (
	value: string | null | undefined,
): CardCategoryValue | null => {
	if (value == null || value === '') {
		return null;
	}

	const normalized = value.trim().toLowerCase();

	if (normalized.startsWith('prepaid')) {
		return CardCategory.PREPAID;
	}

	if (normalized === 'deffered_debit' || normalized === 'deferred_debit') {
		return CardCategory.DEFERRED_DEBIT;
	}

	return normalizeCardCategory(normalized);
};

/**
 * PD-15014 — reduce an untrusted value to a storable card BIN, or null.
 *
 * The BIN (first 6-8 digits) is what every classification service takes, so
 * capturing it at tokenisation is what makes a card classifiable later — the
 * application otherwise stores only the last four. 6-8 digits only, no
 * masking characters: a masked NMI number ("411111******1111") or a full PAN
 * must never land in this column, the former because it classifies nothing
 * and the latter because storing it would drag the database into PCI scope.
 */
export const normalizeCardBin = (
	value: string | null | undefined,
): string | null => {
	if (value == null) {
		return null;
	}

	const trimmed = value.trim();

	return /^\d{6,8}$/.test(trimmed) ? trimmed : null;
};

/**
 * The payment-method type whose funding must be established before a fee may be
 * charged. Declared here rather than imported so this rule stays usable from
 * anywhere — it is consulted by the API response builders, the CRM cart and the
 * storefront alike.
 */
const SURCHARGE_GATED_METHOD_TYPE = 'Credit Card';

/**
 * Whether a processing fee may be charged on a payment method.
 *
 * The question every fee display actually needs answered, and the reason it
 * lives here rather than at each call site: the saved-methods API, the portal
 * checkout, the CRM cart and the storefront all render this, and four copies of
 * the rule is how a customer comes to be quoted a fee that is never charged.
 *
 * Only CARDS are gated. ACH, cash, cheque and account funds are not surcharges
 * under the card-network rules and keep whatever fee they are configured with —
 * treating them like an unclassified card would silently zero a fee those
 * methods legitimately carry.
 *
 * For a card, only `credit` qualifies. Debit and prepaid may not be surcharged,
 * and a card not yet classified is not charged one either, so neither is
 * promised one.
 */
export const isMethodSurchargeable = (
	paymentMethodType: string | null | undefined,
	cardCategory: string | null | undefined,
): boolean =>
	paymentMethodType !== SURCHARGE_GATED_METHOD_TYPE ||
	isSurchargeableCardCategory(cardCategory);
